<template lang="pug">
base-section#home-hero(space='28')
  base-page-content
    .home-hero-shell
      v-row(align='stretch')
        v-col(cols='12', lg='7')
          .home-hero-copy
            base-display-title.home-hero-mark(
              :title='t("pages.home.heroMark")'
              size='compact'
              tone='accent'
              :arrow='true'
            )
            h1.home-hero-title {{ t('pages.home.heroTitle') }}
            p.home-hero-lead {{ t('pages.home.heroLead') }}
            p.home-hero-body {{ t('pages.home.heroBody') }}
            .home-hero-actions
              v-btn(
                class='home-hero-btn-primary'
                color='primary'
                size='large'
                variant='flat'
                :to='localePath("/articles")'
              ) {{ t('pages.home.latestArticles') }}
              v-btn(
                size='large'
                variant='outlined'
                :to='localePath("/persons")'
              ) {{ t('pages.home.meetTeam') }}
              v-btn(
                size='large'
                variant='text'
                :to='localePath("/publications")'
              ) {{ t('pages.home.publications') }}
            .home-hero-metrics
              article.metric-card(v-for='metric in metrics', :key='metric.label')
                p.metric-value {{ rt(metric.value) }}
                p.metric-label {{ rt(metric.label) }}
        v-col(cols='12', lg='5')
          .home-hero-visual
            .hero-logo-card
              base-display-title.hero-logo-kicker(
                :title='t("pages.home.university")'
                size='compact'
                font='sans'
                tone='subtle'
                :arrow='true'
              )
              base-img.hero-logo(
                src='/media/brand/EoT-blue-full-3.png'
                alt='Enterprise of Things Lab logo'
                contain
                max-width='440px'
                sizes='xs:100vw sm:100vw md:420px lg:440px'
              )
            .hero-focus-grid
              article.focus-card(v-for='focus in focusAreas', :key='focus.title')
                p.focus-index {{ focus.index }}
                h2.focus-title {{ rt(focus.title) }}
                p.focus-copy {{ rt(focus.copy) }}
            .hero-latest(v-if='featuredArticles.length')
              .hero-latest-heading
                base-display-title(:title='t("pages.home.latest")', size='compact')
                span {{ t('pages.home.recentWork') }}
              nuxt-link.hero-latest-item(
                v-for='article in featuredArticles'
                :key='article.path'
                :to='localePath(article.path)'
              )
                strong {{ article.title }}
                span {{ article.description }}
</template>

<script setup lang="ts">
defineOptions({
  name: 'SectionHomeHero',
})

const props = defineProps({
  articles: {
    type: Array,
    default: () => [],
  },
})

const { tm, rt, t } = useI18n()
const localePath = useLocalePath()

const metrics = computed(
  () => tm('pages.home.metrics') as Array<Record<string, string>>,
)
const focusAreas = computed(
  () => tm('pages.home.focusAreas') as Array<Record<string, string>>,
)

const featuredArticles = computed(() => (props.articles ?? []).slice(0, 3))
</script>

<style scoped>
#home-hero {
  background:
    radial-gradient(
      circle at top left,
      rgba(0, 192, 216, 0.14),
      transparent 32rem
    ),
    linear-gradient(180deg, var(--surface-soft) 0%, var(--surface-page) 24rem);
}

.home-hero-shell {
  padding: 1.5rem;
  border: 1px solid var(--border-default);
  background:
    linear-gradient(
      180deg,
      color-mix(in srgb, var(--surface-card) 96%, white) 0%,
      var(--surface-card) 100%
    ),
    radial-gradient(
      circle at top right,
      rgba(0, 192, 216, 0.08),
      transparent 20rem
    );
  box-shadow: var(--shadow-soft);
}

.home-hero-copy {
  display: grid;
  align-content: start;
  gap: 1rem;
  padding-right: 1rem;
}

.home-hero-mark {
  color: var(--color-accent-soft);
}

.home-hero-title {
  margin: 0;
  max-width: 14ch;
  font-size: clamp(2.8rem, 6vw, 5rem);
  line-height: 0.92;
  letter-spacing: -0.06em;
}

.home-hero-lead {
  max-width: 46rem;
  margin: 0;
  font-size: clamp(1.05rem, 1.8vw, 1.3rem);
  line-height: 1.55;
  color: var(--color-text);
}

.home-hero-body {
  max-width: 44rem;
  margin: 0;
  color: var(--color-text-muted);
}

.home-hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
}

.home-hero-btn-primary {
  color: var(--color-brand-dark) !important;
}

.home-hero-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.9rem;
  margin-top: 0.75rem;
}

.metric-card {
  min-width: 0;
  padding: 1rem 1rem 1.05rem;
  border: 1px solid var(--border-default);
  background: color-mix(in srgb, var(--surface-card) 92%, var(--surface-muted));
  border-radius: var(--radius-md);
}

.metric-value {
  margin: 0 0 0.45rem;
  font-size: 0.84rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--color-text-strong);
}

.metric-label {
  margin: 0;
  color: var(--color-text);
  line-height: 1.5;
}

.home-hero-visual {
  display: grid;
  gap: 1rem;
  align-content: start;
}

.hero-logo-card,
.hero-latest,
.focus-card {
  border: 1px solid var(--border-default);
  background: var(--surface-card);
  border-radius: var(--radius-md);
}

.hero-logo-card {
  padding: 1.2rem;
}

.hero-logo-kicker {
  margin-bottom: 0.75rem;
}

.hero-logo {
  width: 100%;
}

.hero-focus-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.9rem;
}

.focus-card {
  padding: 1rem;
}

.focus-index {
  margin: 0 0 0.7rem;
  color: var(--color-brand-accent-ink);
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.focus-title {
  margin: 0 0 0.55rem;
  font-size: 1rem;
  line-height: 1.1;
}

.focus-copy {
  margin: 0;
  color: var(--color-text-muted);
  font-size: 0.95rem;
  line-height: 1.5;
}

.hero-latest {
  padding: 1rem 1.1rem;
}

.hero-latest-heading {
  display: flex;
  align-items: baseline;
  gap: 0.7rem;
  margin-bottom: 0.8rem;
}

.hero-latest-heading span {
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-text-muted);
}

.hero-latest-item {
  display: grid;
  gap: 0.2rem;
  padding: 0.8rem 0;
  color: inherit;
  border-top: 1px solid var(--border-subtle);
}

.hero-latest-item:first-of-type {
  border-top: 0;
  padding-top: 0;
}

.hero-latest-item strong {
  color: var(--color-text-strong);
  line-height: 1.3;
}

.hero-latest-item span {
  color: var(--color-text-muted);
  font-size: 0.95rem;
  line-height: 1.45;
}

@media (max-width: 1260px) {
  .home-hero-copy {
    padding-right: 0;
  }

  .hero-focus-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 960px) {
  .home-hero-metrics {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .home-hero-shell {
    padding: 1rem;
  }

  .home-hero-title {
    max-width: none;
    font-size: clamp(2.2rem, 12vw, 3.35rem);
  }

  .home-hero-actions {
    display: grid;
  }
}
</style>
